import { Input, InputGroup, InputLeftElement, InputProps } from '@chakra-ui/react';
import {
  ChangeEvent,
  ClipboardEvent,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { cifrasDePegado, MAX_CIFRAS, separarMiles, soloCifras } from '~/utils/dinero';

/**
 * Campo para escribir un precio en pesos: enseña "180.000" mientras se
 * escribe y entrega solo las cifras ("180000") y el número (180000). El valor
 * que se guarda es el mismo de siempre; los puntos son solo de pantalla.
 *
 * Por qué así y no `type="number"`: ese campo no admite el punto de miles,
 * cambia el valor si se gira la rueda del ratón encima y no avisa si se
 * escribe algo raro. Se usa texto con `inputMode="numeric"`, que en el móvil
 * abre el teclado de números (lo que recomiendan las guías de diseño de
 * formularios). El "$" va fijo a la izquierda y no hay que escribirlo; si
 * alguien lo escribe o lo pega, se ignora.
 *
 * Al reformatear se conserva la posición del cursor: se cuenta cuántas cifras
 * había a su izquierda y se vuelve a poner detrás de esas mismas cifras.
 */

type Props = Omit<InputProps, 'value' | 'defaultValue' | 'onChange' | 'type' | 'onPaste'> & {
  /** El número, o sus cifras como texto. Vacío o null deja el campo en blanco. */
  value: number | string | null | undefined;
  /** Cifras sin puntos ("180000", o "" si se vació) y su número (null si vacío). */
  onChange: (cifras: string, numero: number | null) => void;
};

/**
 * El valor recibido → sus cifras. Un número, o un texto que ya es un número
 * ("616250", "616250.5"), se redondea al peso: quitarle el punto a
 * "616250.5" daría 6.162.505. Medido el 13/10/2026: en producción todos los
 * montos son enteros, pero el campo no debe depender de eso.
 */
const aCifras = (v: Props['value']): string => {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : /^\s*\d+(\.\d+)?\s*$/.test(String(v)) ? Number(v) : NaN;
  if (Number.isFinite(n)) return soloCifras(String(Math.round(Math.max(0, n))));
  return soloCifras(String(v));
};

const mostrar = (cifras: string) => (cifras === '' ? '' : separarMiles(Number(cifras)));

/** Posición en el texto con puntos que queda justo detrás de la cifra número `n`. */
const posicionTrasCifras = (texto: string, n: number) => {
  if (n <= 0) return 0;
  let vistas = 0;
  for (let i = 0; i < texto.length; i++) {
    if (/\d/.test(texto[i]) && ++vistas === n) return i + 1;
  }
  return texto.length;
};

const InputPesos = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, size, maxW, w, width, minW, ...resto }, ref) => {
    const [cifras, setCifras] = useState(() => aCifras(value));
    const elemento = useRef<HTMLInputElement | null>(null);
    // Cuántas cifras deben quedar a la izquierda del cursor tras el próximo pintado.
    const cursor = useRef<number | null>(null);
    // Fuerza el pintado aunque las cifras no cambien (p. ej. se tecleó una letra),
    // para recolocar el cursor en vez de dejar que salte al final.
    const [, repintar] = useReducer((x: number) => x + 1, 0);

    // Si el valor cambia desde fuera (otro botón lo rellena, o se reinicia el
    // formulario), el campo lo refleja. Un campo vacío con el valor en 0 no se
    // toca: así se puede borrar todo y escribir de nuevo.
    useEffect(() => {
      const externas = aCifras(value);
      if (externas !== cifras && Number(externas || 0) !== Number(cifras || 0)) setCifras(externas);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useLayoutEffect(() => {
      const el = elemento.current;
      if (cursor.current === null || !el) return;
      if (document.activeElement === el) {
        const pos = posicionTrasCifras(el.value, cursor.current);
        el.setSelectionRange(pos, pos);
      }
      cursor.current = null;
    });

    const aplicar = (todas: string, cifrasAntesDelCursor: number) => {
      const sinCeros = todas.replace(/^0+(?=\d)/, '');
      const quitados = todas.length - sinCeros.length;
      const nuevas = sinCeros.slice(0, MAX_CIFRAS);
      cursor.current = Math.min(nuevas.length, Math.max(0, cifrasAntesDelCursor - quitados));
      setCifras(nuevas);
      repintar();
      if (nuevas !== cifras) onChange(nuevas, nuevas === '' ? null : Number(nuevas));
    };

    const alCambiar = (e: ChangeEvent<HTMLInputElement>) => {
      const el = e.target;
      const texto = el.value;
      const pos = el.selectionStart ?? texto.length;
      let todas = texto.replace(/\D/g, '');
      let antes = texto.slice(0, pos).replace(/\D/g, '').length;
      // Borrar justo un punto no cambia ninguna cifra: se borra la cifra de al
      // lado, que es lo que la persona quería.
      const tipo = (e.nativeEvent as InputEvent)?.inputType ?? '';
      if (todas === cifras && tipo === 'deleteContentBackward' && antes > 0) {
        todas = todas.slice(0, antes - 1) + todas.slice(antes);
        antes -= 1;
      } else if (todas === cifras && tipo === 'deleteContentForward' && antes < todas.length) {
        todas = todas.slice(0, antes) + todas.slice(antes + 1);
      }
      aplicar(todas, antes);
    };

    // Lo pegado puede traer "$", espacios o centavos ("180.000,00").
    const alPegar = (e: ClipboardEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      const inicio = el.selectionStart ?? el.value.length;
      const fin = el.selectionEnd ?? inicio;
      const izquierda = el.value.slice(0, inicio).replace(/\D/g, '');
      const derecha = el.value.slice(fin).replace(/\D/g, '');
      const pegado = cifrasDePegado(e.clipboardData.getData('text'));
      e.preventDefault();
      aplicar(izquierda + pegado + derecha, izquierda.length + pegado.length);
    };

    return (
      <InputGroup size={size} maxW={maxW} w={w ?? width} minW={minW}>
        <InputLeftElement pointerEvents='none' color='gray.500' aria-hidden='true'>
          $
        </InputLeftElement>
        <Input
          {...resto}
          ref={(nodo) => {
            elemento.current = nodo;
            if (typeof ref === 'function') ref(nodo);
            else if (ref) ref.current = nodo;
          }}
          type='text'
          inputMode='numeric'
          autoComplete='off'
          value={mostrar(cifras)}
          onChange={alCambiar}
          onPaste={alPegar}
        />
      </InputGroup>
    );
  }
);

InputPesos.displayName = 'InputPesos';

export default InputPesos;
